import { DURACIONES_MINUTOS } from '@padel/domain';

import type { FranjaPrecioPublica } from '@/infra/contratos';
import { precio } from '@/publico/dominio/formato';

interface Propiedades {
  readonly tarifas: readonly FranjaPrecioPublica[];
  readonly cargando: boolean;
  readonly error: boolean;
}

interface FilaPrecio {
  readonly desdeLocalHora: string;
  readonly hastaLocalHoraExclusiva: string;
  readonly precios: readonly (number | undefined)[];
  readonly franjas: readonly FranjaPrecioPublica[];
}

/** Resume las franjas generales; las duraciones ausentes se explican al pie. */
function filasDe(tarifas: readonly FranjaPrecioPublica[]): readonly FilaPrecio[] {
  const filas: FilaPrecio[] = [];
  for (const franja of [...tarifas].sort((a, b) =>
    a.desdeLocalHora.localeCompare(b.desdeLocalHora),
  )) {
    const precios = DURACIONES_MINUTOS.map(
      (duracion) =>
        franja.opciones.find((opcion) => opcion.duracionMinutos === duracion)?.precioCentesimos,
    );
    const anterior = filas.at(-1);
    if (
      franja.desdeLocalHora === anterior?.hastaLocalHoraExclusiva &&
      precios.some(
        (importe, indice) => importe !== undefined && importe === anterior.precios[indice],
      ) &&
      precios.every(
        (importe, indice) =>
          importe === undefined ||
          anterior.precios[indice] === undefined ||
          importe === anterior.precios[indice],
      )
    ) {
      filas[filas.length - 1] = {
        ...anterior,
        hastaLocalHoraExclusiva: franja.hastaLocalHoraExclusiva,
        precios: precios.map((importe, indice) => importe ?? anterior.precios[indice]),
        franjas: [...anterior.franjas, franja],
      };
    } else {
      filas.push({ ...franja, precios, franjas: [franja] });
    }
  }
  return filas;
}

export function PreciosPorHorario({ tarifas, cargando, error }: Propiedades): React.JSX.Element {
  const filas = filasDe(tarifas);
  const excepciones = filas.flatMap((fila) =>
    fila.franjas.flatMap((franja) => {
      const ausentes = DURACIONES_MINUTOS.filter(
        (duracion, indice) =>
          fila.precios[indice] !== undefined &&
          !franja.opciones.some((opcion) => opcion.duracionMinutos === duracion),
      );
      return ausentes.length === 0
        ? []
        : [
            `De ${franja.desdeLocalHora} a ${franja.hastaLocalHoraExclusiva} no se ofrecen turnos de ${ausentes.join(' ni ')} min.`,
          ];
    }),
  );
  return (
    <aside aria-labelledby="titulo-precios" className="precios-horario">
      <div className="precios-horario__cabecera">
        <h2 id="titulo-precios">Tarifas de las canchas</h2>
        <span className="precios-horario__moneda">UYU</span>
      </div>
      <p className="precios-horario__bajada">La misma tarifa para ambas canchas.</p>
      {cargando ? (
        <p className="precios-horario__estado">Consultando precios…</p>
      ) : error || filas.length === 0 ? (
        <p className="precios-horario__estado">
          Las tarifas generales no están disponibles en este momento.
        </p>
      ) : (
        <>
          <table className="precios-horario__tabla">
            <caption className="solo-lectores">
              Tarifas generales por hora de inicio, en pesos uruguayos
            </caption>
            <thead>
              <tr>
                <th scope="col">Horario</th>
                {DURACIONES_MINUTOS.map((duracion) => (
                  <th key={duracion} scope="col">
                    {duracion} min
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila) => (
                <tr key={fila.desdeLocalHora}>
                  <th scope="row">
                    {fila.desdeLocalHora} a {fila.hastaLocalHoraExclusiva} h
                  </th>
                  {fila.precios.map((importe, indice) => (
                    <td key={DURACIONES_MINUTOS[indice]}>
                      {importe === undefined ? (
                        <span className="precios-horario__no-ofrecido">No se ofrece</span>
                      ) : (
                        precio(importe)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {excepciones.map((nota) => (
            <p className="precios-horario__nota" key={nota}>
              {nota}
            </p>
          ))}
        </>
      )}
      <p className="precios-horario__nota">Tarifa según la hora de inicio del turno.</p>
    </aside>
  );
}
